/**
 * useAdaptiveDifficulty Hook
 * 
 * Dynamically adjusts question difficulty based on user performance.
 * Tracks accuracy, time per question, and hints used to recommend
 * appropriate difficulty levels.
 * 
 * @module useAdaptiveDifficulty
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useGameProgress } from './useGameProgress';
// Lazy import logger to avoid initialization order issues
// import { logger } from '../utils/logger';

/**
 * Difficulty levels
 */
export const DIFFICULTY_LEVELS = {
  EASY: 'easy',
  MEDIUM: 'medium',
  HARD: 'hard'
};

/**
 * Performance thresholds for difficulty adjustment
 */
const PERFORMANCE_THRESHOLDS = {
  EASY: {
    minAccuracy: 0.6, // 60% accuracy
    maxTimePerQuestion: 30000, // 30 seconds
    maxHintsUsed: 2
  },
  MEDIUM: {
    minAccuracy: 0.7, // 70% accuracy
    maxTimePerQuestion: 45000, // 45 seconds
    maxHintsUsed: 1
  },
  HARD: {
    minAccuracy: 0.8, // 80% accuracy
    maxTimePerQuestion: 60000, // 60 seconds
    maxHintsUsed: 0
  }
};

/**
 * Hook for adaptive difficulty management
 * 
 * @param {string} subject - Current subject identifier
 * @param {Object} [options] - Configuration options
 * @param {number} [options.minSamples=3] - Minimum samples needed for adjustment
 * @param {boolean} [options.autoAdjust=true] - Whether to auto-adjust difficulty
 * @returns {Object} Difficulty state and management functions
 * 
 * @example
 * const { difficulty, recommendedDifficulty, adjustDifficulty, performanceMetrics } = useAdaptiveDifficulty('math');
 */
export const useAdaptiveDifficulty = (subject, options = {}) => {
  const { minSamples = 3, autoAdjust = true } = options;
  const { progress } = useGameProgress();
  
  // Cache lazy-loaded logger
  const loggerRef = useRef(null);
  
  const getLogger = async () => {
    if (!loggerRef.current) {
      loggerRef.current = (await import('../utils/logger'));
    }
    return loggerRef.current.logger;
  };
  
  const [difficulty, setDifficulty] = useState(() => {
    // Load saved difficulty preference for this subject
    if (!progress || !progress.adaptiveDifficulty) {
      return DIFFICULTY_LEVELS.MEDIUM;
    }
    const saved = progress.adaptiveDifficulty[subject]?.difficulty || DIFFICULTY_LEVELS.MEDIUM;
    return saved;
  });

  const [recommendedDifficulty, setRecommendedDifficulty] = useState(DIFFICULTY_LEVELS.MEDIUM);

  /**
   * Calculate performance metrics for recent questions
   * 
   * @returns {Object} Performance metrics
   */
  const performanceMetrics = useMemo(() => {
    if (!progress || !subject) {
      return {
        accuracy: 0,
        averageTime: 0,
        hintsUsed: 0,
        sampleCount: 0
      };
    }
    const subjectProgress = progress[subject] || {};
    const recentScores = subjectProgress.recentScores || [];
    const recentTimings = subjectProgress.recentTimings || [];
    const recentHints = subjectProgress.recentHints || [];
    
    if (recentScores.length < minSamples) {
      return {
        averageScore: 0,
        averageAccuracy: 0,
        averageTime: 0,
        averageHintsUsed: 0,
        sampleSize: recentScores.length,
        hasEnoughData: false
      };
    }

    // Calculate averages
    const avgScore = recentScores.reduce((a, b) => a + b, 0) / recentScores.length;
    const avgAccuracy = recentScores.filter(s => s >= 80).length / recentScores.length;
    const avgTime = recentTimings.length > 0 
      ? recentTimings.reduce((a, b) => a + b, 0) / recentTimings.length 
      : 0;
    const avgHints = recentHints.length > 0
      ? recentHints.reduce((a, b) => a + b, 0) / recentHints.length
      : 0;

    return {
      averageScore: avgScore,
      averageAccuracy: avgAccuracy,
      averageTime: avgTime,
      averageHintsUsed: avgHints,
      sampleSize: recentScores.length,
      hasEnoughData: true
    };
  }, [progress, subject, minSamples]);

  /**
   * Calculate recommended difficulty based on performance
   * 
   * @returns {string} Recommended difficulty level
   */
  const calculateRecommendedDifficulty = useCallback(() => {
    if (!performanceMetrics.hasEnoughData) {
      return DIFFICULTY_LEVELS.MEDIUM;
    }

    const { averageAccuracy, averageTime, averageHintsUsed } = performanceMetrics;

    // Check if ready for harder difficulty
    if (difficulty === DIFFICULTY_LEVELS.EASY || difficulty === DIFFICULTY_LEVELS.MEDIUM) {
      const hardThreshold = PERFORMANCE_THRESHOLDS.HARD;
      if (
        averageAccuracy >= hardThreshold.minAccuracy &&
        averageTime <= hardThreshold.maxTimePerQuestion &&
        averageHintsUsed <= hardThreshold.maxHintsUsed
      ) {
        return difficulty === DIFFICULTY_LEVELS.EASY 
          ? DIFFICULTY_LEVELS.MEDIUM 
          : DIFFICULTY_LEVELS.HARD;
      }
    }

    // Check if should stay at current difficulty (medium)
    if (difficulty === DIFFICULTY_LEVELS.MEDIUM) {
      const mediumThreshold = PERFORMANCE_THRESHOLDS.MEDIUM;
      if (
        averageAccuracy >= mediumThreshold.minAccuracy &&
        averageTime <= mediumThreshold.maxTimePerQuestion &&
        averageHintsUsed <= mediumThreshold.maxHintsUsed
      ) {
        return DIFFICULTY_LEVELS.MEDIUM;
      }
    }

    // Check if should reduce difficulty
    if (difficulty === DIFFICULTY_LEVELS.HARD || difficulty === DIFFICULTY_LEVELS.MEDIUM) {
      const easyThreshold = PERFORMANCE_THRESHOLDS.EASY;
      if (
        averageAccuracy < easyThreshold.minAccuracy ||
        averageTime > easyThreshold.maxTimePerQuestion ||
        averageHintsUsed > easyThreshold.maxHintsUsed
      ) {
        return difficulty === DIFFICULTY_LEVELS.HARD 
          ? DIFFICULTY_LEVELS.MEDIUM 
          : DIFFICULTY_LEVELS.EASY;
      }
    }

    // Default: maintain current difficulty
    return difficulty;
  }, [performanceMetrics, difficulty]);

  /**
   * Update recommended difficulty based on performance
   */
  useEffect(() => {
    if (autoAdjust && performanceMetrics.hasEnoughData) {
      const recommended = calculateRecommendedDifficulty();
      setRecommendedDifficulty(recommended);
      
      // Auto-adjust if different from current
      if (autoAdjust && recommended !== difficulty) {
        getLogger().then(logger => logger.info(`Adaptive difficulty: ${difficulty} → ${recommended} for ${subject}`)).catch(() => {});
        setDifficulty(recommended);
      }
    }
  }, [performanceMetrics, calculateRecommendedDifficulty, autoAdjust, difficulty, subject]);

  /**
   * Manually adjust difficulty
   * 
   * @param {string} newDifficulty - New difficulty level
   */
  const adjustDifficulty = useCallback((newDifficulty) => {
    if (Object.values(DIFFICULTY_LEVELS).includes(newDifficulty)) {
      setDifficulty(newDifficulty);
      getLogger().then(logger => logger.info(`Difficulty manually adjusted to ${newDifficulty} for ${subject}`)).catch(() => {});
    }
  }, [subject]);

  /**
   * Get personalized recommendations based on performance
   * 
   * @returns {Object} Recommendations object
   */
  const getRecommendations = useCallback(() => {
    if (!performanceMetrics.hasEnoughData) {
      return {
        message: 'Keep practicing to get personalized recommendations!',
        suggestedTopics: [],
        focusAreas: []
      };
    }

    const { averageAccuracy, averageTime, averageHintsUsed } = performanceMetrics;
    const recommendations = {
      message: '',
      suggestedTopics: [],
      focusAreas: []
    };

    // Speed recommendations
    if (averageTime > PERFORMANCE_THRESHOLDS.MEDIUM.maxTimePerQuestion) {
      recommendations.focusAreas.push('Speed');
      recommendations.message = 'Try to answer questions more quickly.';
    }

    // Accuracy recommendations
    if (averageAccuracy < PERFORMANCE_THRESHOLDS.MEDIUM.minAccuracy) {
      recommendations.focusAreas.push('Accuracy');
      recommendations.message = 'Focus on getting the correct answer.';
    }

    // Hint usage recommendations
    if (averageHintsUsed > PERFORMANCE_THRESHOLDS.MEDIUM.maxHintsUsed) {
      recommendations.focusAreas.push('Independence');
      recommendations.message = 'Try to answer without using hints.';
    }

    // Positive feedback
    if (
      averageAccuracy >= PERFORMANCE_THRESHOLDS.HARD.minAccuracy &&
      averageTime <= PERFORMANCE_THRESHOLDS.HARD.maxTimePerQuestion &&
      averageHintsUsed <= PERFORMANCE_THRESHOLDS.HARD.maxHintsUsed
    ) {
      recommendations.message = 'Great job! You\'re ready for harder questions!';
      recommendations.suggestedTopics = ['Challenge yourself with harder questions'];
    }

    return recommendations;
  }, [performanceMetrics]);

  return {
    difficulty,
    recommendedDifficulty,
    adjustDifficulty,
    performanceMetrics,
    getRecommendations,
    hasEnoughData: performanceMetrics.hasEnoughData
  };
};

